import { CONTRACT_STATUSES, CURRENCIES, MONGO_FILTER_OPERATORS } from '@vy/types';
import { useMemo } from 'react';
import type { FilterField, FilterOption } from '../../../../components/new-design/vyTableFilter';
import { useCatalogProductsList } from '../../../../hooks/requests/use-catalog-products-list';
import { useMeters } from '../../../../hooks/requests/use-meters/use-meters';
import {
  COMMON_FILTER_FIELD_IDS,
  CONTRACT_STATUS_FILTER_FIELD_ID,
  PRODUCT_FILTER_FIELD_ID,
} from './reports-filter-field-ids';

export const MULTISELECT_OPERATORS = [
  { label: 'Is any of', value: MONGO_FILTER_OPERATORS.IN },
  { label: 'Is not any of', value: MONGO_FILTER_OPERATORS.NOT_IN },
];
export const DATE_PRESET_OPERATORS = [{ label: 'Is', value: 'IS' }];

export const TEXT_OPERATORS = [{ label: 'Contains', value: MONGO_FILTER_OPERATORS.REGEX }];

export function buildTextFilterField(id: string, label: string): FilterField {
  return {
    id,
    label,
    inputType: 'text',
    operators: TEXT_OPERATORS,
  };
}

export function buildCustomerFilterField(options: FilterOption[]): FilterField {
  return {
    id: COMMON_FILTER_FIELD_IDS.Customer,
    label: 'Customer',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options,
  };
}

export function buildCountryFilterField(options: FilterOption[]): FilterField {
  return {
    id: COMMON_FILTER_FIELD_IDS.Country,
    label: 'Country',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options,
  };
}

export function buildStateFilterField(options: FilterOption[]): FilterField {
  return {
    id: COMMON_FILTER_FIELD_IDS.State,
    label: 'State',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options,
  };
}

export function buildLabelsFilterField(options: FilterOption[]): FilterField {
  return {
    id: COMMON_FILTER_FIELD_IDS.Labels,
    label: 'Labels',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options,
  };
}

export function buildPlanFilterField(options: FilterOption[]): FilterField {
  return {
    id: COMMON_FILTER_FIELD_IDS.Plan,
    label: 'Plan',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options,
  };
}

export function buildContractStartDateFilterField(): FilterField {
  return {
    id: COMMON_FILTER_FIELD_IDS.ContractStartDate,
    label: 'Contract Start Date',
    inputType: 'date-preset',
    operators: DATE_PRESET_OPERATORS,
  };
}

export function buildContractStatusFilterField(): FilterField {
  return {
    id: CONTRACT_STATUS_FILTER_FIELD_ID,
    label: 'Contract Status',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options: Object.values(CONTRACT_STATUSES).map((status) => ({ value: status, label: status })),
  };
}

export function buildCurrencyFilterField(fieldId: string): FilterField {
  return {
    id: fieldId,
    label: 'Currency',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options: Object.values(CURRENCIES).map((currency) => ({ value: currency, label: currency })),
  };
}

export function buildLabelsOptions(
  allLabels: { id: string; name: string }[] | undefined,
  presentLabelIds: Set<string>,
): FilterOption[] {
  return (allLabels ?? [])
    .filter((label) => presentLabelIds.has(label.id))
    .map((label) => ({ value: label.id, label: label.name }));
}

export function buildProductFilterField(options: FilterOption[]): FilterField {
  return {
    id: PRODUCT_FILTER_FIELD_ID,
    label: 'Products',
    inputType: 'select',
    operators: MULTISELECT_OPERATORS,
    options,
  };
}

export function useProductFilterField(): FilterField {
  const { data: catalogProductsData } = useCatalogProductsList();
  const { data: metersData } = useMeters();

  return useMemo(() => {
    const catalogProductOptions = (catalogProductsData?.catalogProducts ?? []).map((product) => ({
      value: `catalog:${product.id}`,
      label: product.name,
    }));

    const meterOptions = (metersData?.meters ?? []).map((meter) => ({
      value: `meter:${meter.id}`,
      label: `${meter.name} (Meter)`,
    }));

    const productOptions = [...catalogProductOptions, ...meterOptions].sort((a, b) =>
      a.label.localeCompare(b.label),
    );

    return {
      id: PRODUCT_FILTER_FIELD_ID,
      label: 'Products',
      inputType: 'select',
      operators: MULTISELECT_OPERATORS,
      options: productOptions,
    };
  }, [catalogProductsData, metersData]);
}

export function filterNonEmptySelectFields(fields: FilterField[]): FilterField[] {
  return fields.filter((field) => {
    if (field.inputType !== 'select') {
      return true;
    }

    if (field.id === PRODUCT_FILTER_FIELD_ID) {
      return true;
    }

    return (field.options?.length ?? 0) >= 2;
  });
}
